import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import type { ListingCrop } from './listingCropsRun';

// WHICH PHOTOGRAPHS OF THE PRODUCT GO WITH ONE PART'S ORIENTATION QUESTION.
//
// The call matches six renders of a mesh against pictures of the assembled thing, and which
// pictures those are was decided entirely by the classification: the front, the back, one
// profile. On a chair whose rear leg frame is found in the three-quarter shots and in the
// close-up and in NEITHER of the square-on views, that is a question asked over two
// photographs the part cannot be seen in — a model told to match a leg against a picture that
// does not show it, twice, and then asked which way round it goes.
//
// So the picture set follows the CROPS as well as the classification (see askPictures), and
// this is where the person on the page settles the rest: every picture in the prompt carries
// its own toggle, and what is stored here is their answer.
//
// THE DEFAULT IS DERIVED, NOT STORED. A picture whose every crop for this part has been
// struck through shows nothing of it — somebody has already looked and said so — and it opens
// excluded. A picture with live crops, or with no crops at all, opens included: the first
// shows the part, and the second at least shows the product's own frame, which is what the
// front and back views are for. Storing the answer instead of deriving it would mean a crop
// struck through tomorrow leaves yesterday's "included" standing over it.
//
// Persisted like every other answer about a document, so it is on the server and the other
// admin sees the same question.

// Part index -> photograph URL -> whether it goes. Only what somebody has explicitly said:
// an absent entry is the derived default above, and that is the whole difference between a
// list that goes stale and one that cannot.
export type ByPartShots = Record<number, Record<string, boolean>>;

const key = (docId: string) => `notice.orientShots.${docId}`;

const store = createDocEntryStore<{ byPart: ByPartShots }>((docId) => ({
  snapshot: { byPart: readState<ByPartShots>(docId, key(docId), {}) },
}));

export const useOrientShots = (docId: string): ByPartShots => store.useSnapshot(docId).byPart;

// Whether a photograph shows this part at all, as the page has it: at least one crop nobody
// has struck through. The same test that decides whether the prompt says "with this part
// outlined in green", so a picture that says nothing about the part cannot claim to.
export const showsPart = (crops: ListingCrop[]): boolean => crops.some((c) => !c.off);

// What a picture does when nobody has said. See the note above: struck through everywhere is
// the one case that opens excluded.
const defaultsOn = (crops: ListingCrop[]): boolean => crops.length === 0 || showsPart(crops);

// Whether this photograph goes with this part's question — somebody's answer if they gave
// one, the derived default if they did not.
export const shotIsOn = (
  byPart: ByPartShots, part: number, url: string, crops: ListingCrop[],
): boolean => byPart[part]?.[url] ?? defaultsOn(crops);

// Say so. Written through immediately, like a crop being struck through: it is a judgement
// somebody made by looking, and it costs more to make again than it does to keep.
export function setOrientShot(docId: string, part: number, url: string, on: boolean): void {
  const byPart = store.get(docId).byPart;
  const next: ByPartShots = { ...byPart, [part]: { ...(byPart[part] ?? {}), [url]: on } };
  writeState(docId, key(docId), next);
  store.patch(docId, { byPart: next });
}
